import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ShieldCheck, Mail, Loader2, Copy } from 'lucide-react';

interface MFAEnrollProps {
  onEnrolled: () => void;
  onCancelled?: () => void;
}

type EnrollStep = 'choose' | 'totp' | 'email-verify';

export default function MFAEnroll({ onEnrolled, onCancelled }: MFAEnrollProps) {
  const [step, setStep] = useState<EnrollStep>('choose');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [sending, setSending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const { toast } = useToast();

  const chooseTOTP = async () => {
    setStep('totp');
    setEnrolling(true);

    try {
      // Remove any existing unverified TOTP factors first
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const unverifiedTOTP = factors?.totp?.filter(f => (f.status as string) !== 'verified') ?? [];
      for (const factor of unverifiedTOTP) {
        const { error: unenrollErr } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        if (unenrollErr) {
          console.warn('Failed to unenroll factor:', factor.id, unenrollErr.message);
        }
      }

      // Small delay to ensure unenroll is processed
      if (unverifiedTOTP.length > 0) {
        await new Promise(r => setTimeout(r, 500));
      }

      // Always use a unique friendly name to avoid conflicts with existing verified factors
      const uniqueName = `Admin Authenticator ${Date.now()}`;
      let enrollResult = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: uniqueName,
      });

      if (enrollResult.error) {
        toast({
          title: 'Fehler bei der 2FA-Einrichtung',
          description: enrollResult.error.message,
          variant: 'destructive',
        });
        setStep('choose');
        setEnrolling(false);
        return;
      }

      setFactorId(enrollResult.data.id);
      setQrCode(enrollResult.data.totp.qr_code);
      setSecret(enrollResult.data.totp.secret);
    } catch (err) {
      toast({ title: 'Fehler', description: 'TOTP-Einrichtung fehlgeschlagen.', variant: 'destructive' });
      setStep('choose');
    } finally {
      setEnrolling(false);
    }
  };

  const chooseEmail = async () => {
    setStep('email-verify');

    // Enable email OTP in shop_settings
    const { data: existing } = await supabase
      .from('shop_settings')
      .select('id')
      .eq('key', 'admin_mfa_email_otp_enabled')
      .maybeSingle();

    if (existing) {
      await supabase
        .from('shop_settings')
        .update({ value: { enabled: true } as unknown as Json, updated_at: new Date().toISOString() })
        .eq('key', 'admin_mfa_email_otp_enabled');
    } else {
      await supabase
        .from('shop_settings')
        .insert([{ key: 'admin_mfa_email_otp_enabled', value: { enabled: true } as unknown as Json }]);
    }
  };

  const sendEmailCode = async () => {
    setSending(true);
    try {
      const response = await supabase.functions.invoke('send-admin-otp', {
        body: { action: 'send' },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);

      setMaskedEmail(response.data.email || '');
      setCodeSent(true);
      toast({ title: 'Code gesendet', description: 'Prüfe dein E-Mail-Postfach.' });
    } catch (error) {
      toast({
        title: 'Fehler',
        description: error instanceof Error ? error.message : 'Code konnte nicht gesendet werden.',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const verifyEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await supabase.functions.invoke('send-admin-otp', {
        body: { action: 'verify', code: verifyCode },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);

      if (response.data?.success) {
        toast({ title: '2FA aktiviert', description: 'E-Mail-Verifizierung wurde eingerichtet.' });
        onEnrolled();
      } else {
        throw new Error('Verifizierung fehlgeschlagen');
      }
    } catch (error) {
      toast({
        title: 'Code ungültig',
        description: error instanceof Error ? error.message : 'Bitte überprüfe den Code.',
        variant: 'destructive',
      });
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const challenge = await supabase.auth.mfa.challenge({ factorId });
    if (challenge.error) {
      toast({ title: 'Challenge fehlgeschlagen', description: challenge.error.message, variant: 'destructive' });
      setLoading(false);
      return;
    }

    const verify = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code: verifyCode,
    });

    if (verify.error) {
      toast({ title: 'Code ungültig', description: 'Bitte überprüfe den Code und versuche es erneut.', variant: 'destructive' });
      setVerifyCode('');
      setLoading(false);
      return;
    }

    toast({ title: '2FA aktiviert', description: 'Authenticator-App wurde eingerichtet.' });
    onEnrolled();
    setLoading(false);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast({ title: 'Secret kopiert' });
  };

  // Step: Choose method
  if (step === 'choose') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">2FA einrichten</CardTitle>
            <CardDescription>
              Wähle deine bevorzugte Verifizierungsmethode
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full h-auto py-4 px-4 flex items-center gap-4 justify-start"
              onClick={chooseTOTP}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="font-medium">Authenticator-App</div>
                <div className="text-sm text-muted-foreground font-normal">Code aus Google Authenticator, Authy o.ä.</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="w-full h-auto py-4 px-4 flex items-center gap-4 justify-start"
              onClick={chooseEmail}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                <Mail className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="font-medium">E-Mail-Code</div>
                <div className="text-sm text-muted-foreground font-normal">Bestätigungscode per E-Mail erhalten</div>
              </div>
            </Button>

            {onCancelled && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  onClick={onCancelled}
                >
                  Abmelden
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Email OTP enrollment
  if (step === 'email-verify') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">E-Mail-Verifizierung einrichten</CardTitle>
            <CardDescription>
              {codeSent
                ? `Ein 6-stelliger Code wurde an ${maskedEmail} gesendet`
                : 'Wir senden dir einen Bestätigungscode per E-Mail, um die Methode zu aktivieren'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!codeSent ? (
              <div className="space-y-4">
                <Button onClick={sendEmailCode} className="w-full" disabled={sending}>
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sende Code...</>
                  ) : (
                    'Code per E-Mail senden'
                  )}
                </Button>
              </div>
            ) : (
              <form onSubmit={verifyEmailCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-enroll-code">Bestätigungscode</Label>
                  <Input
                    id="email-enroll-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading || verifyCode.length !== 6}>
                  {loading ? 'Überprüfen...' : '2FA aktivieren'}
                </Button>
                <div className="text-center">
                  <button type="button" className="text-sm text-muted-foreground hover:text-primary transition-colors" onClick={sendEmailCode} disabled={sending}>
                    {sending ? 'Sende...' : 'Code erneut senden'}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-4 flex flex-col items-center gap-2">
              <button type="button" className="text-sm text-muted-foreground hover:text-primary transition-colors" onClick={() => { setStep('choose'); setCodeSent(false); setVerifyCode(''); }}>
                Andere Methode wählen
              </button>
              {onCancelled && (
                <button type="button" className="text-sm text-muted-foreground hover:text-primary transition-colors" onClick={onCancelled}>
                  Abmelden
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: TOTP enrollment
  if (enrolling) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Authenticator einrichten</CardTitle>
          <CardDescription>
            Scanne den QR-Code mit deiner Authenticator-App (z.B. Google Authenticator, Authy)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {qrCode && (
            <div className="flex justify-center">
              <img src={qrCode} alt="QR Code für 2FA" className="w-48 h-48 rounded-lg border" />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Oder Secret manuell eingeben:</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-muted p-2 rounded font-mono break-all">{secret}</code>
              <Button type="button" variant="outline" size="icon" onClick={copySecret}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <form onSubmit={handleVerifyTOTP} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="totp-code">Bestätigungscode</Label>
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || verifyCode.length !== 6}>
              {loading ? 'Überprüfen...' : '2FA aktivieren'}
            </Button>
          </form>

          <div className="flex flex-col items-center gap-2">
            <button type="button" className="text-sm text-muted-foreground hover:text-primary transition-colors" onClick={() => { setStep('choose'); setVerifyCode(''); }}>
              Andere Methode wählen
            </button>
            {onCancelled && (
              <button type="button" className="text-sm text-muted-foreground hover:text-primary transition-colors" onClick={onCancelled}>
                Abmelden
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
